/**
 * Map a raw change-password error (the backend sends plain HTTPException
 * message strings, not codes) to instructive, user-facing copy — so the
 * FormAlert never shows a bare "Invalid request" for a known failure (#44).
 *
 * Pure + unit-tested (node env). The component just renders `.message` and,
 * when present, the `.hint` affordance.
 *
 * NOTE — backend gap: there is no "recently used" (password history) or
 * "compromised" (breach/HIBP) check on the backend today. The last two
 * entries below are FORWARD-READY: when the backend adds those checks and
 * returns the matching code/message, the UI already maps them. Until then
 * they're inert. Adding the backend checks is a separate (medium) follow-up.
 */
export interface MappedPasswordError {
  message: string;
  hint?: 'forgot_password';
}

/** Shared copy for any strength-rule failure that slips past the client-side
 *  policy gate (e.g. client/backend policy drift). The live checklist on the
 *  form is the primary surface; this is the fallback. */
const STRENGTH =
  'Your password must be at least 12 characters and include uppercase, lowercase, a number, and a symbol.';

const ERROR_MAP: Record<string, MappedPasswordError> = {
  // Identity / flow errors.
  'Current password is incorrect': {
    message: 'That password is incorrect.',
    hint: 'forgot_password',
  },
  'Cannot change password on OAuth/external accounts': {
    message:
      'This account signs in with another account, such as Google, Microsoft or Apple, so it has no password to change here.',
  },
  'New password must differ from the current one': {
    message: 'Your new password must be different from the current one.',
  },
  // Strength rules (mirror app/api/v1/routes/auth.py policy messages).
  'Password must be at least 12 characters long': { message: STRENGTH },
  'Must include at least one uppercase letter': { message: STRENGTH },
  'Must include at least one lowercase letter': { message: STRENGTH },
  'Must include at least one digit': { message: STRENGTH },
  'Must include at least one special character': { message: STRENGTH },
  // Forward-ready (see NOTE above) — no sensitive detail leaked (#44 AC).
  password_recently_used: {
    message: 'Your new password must be different from your last 5 passwords.',
  },
  password_compromised: {
    message: 'This password has appeared in a known data breach. Please choose a different one.',
  },
};

/** Instructive message (+ optional hint) for a raw backend error string.
 *  Unknown errors fall back to the raw backend message — which is already a
 *  specific sentence, never a generic "Invalid request". */
export function mapPasswordError(raw: string): MappedPasswordError {
  return ERROR_MAP[raw] ?? { message: raw };
}
